import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Lightbulb, X, ChevronRight } from 'lucide-react'
import { useTour } from '@/components/TourProvider'
import { useTourHandler } from '@/contexts/tour-handler'
import { useQuickHelpContext } from '@/contexts/quick-help'
import { getAllTours, markTourCompleted } from '@/services/tourService'
import { FeatureTour } from '@/types/tour'
import { cn } from '@/lib/utils'

const HIDDEN_HINTS_KEY = 'datacentral_hidden_tour_hints'

/** Get list of tour IDs that user chose to hide permanently */
const getHiddenHints = (): string[] => {
    try {
        const stored = localStorage.getItem(HIDDEN_HINTS_KEY)
        return stored ? JSON.parse(stored) : []
    } catch {
        return []
    }
}

/** Save tour ID to hidden list */
const hideHintPermanently = (tourId: string): void => {
    const hidden = getHiddenHints()
    if (!hidden.includes(tourId)) {
        hidden.push(tourId)
        localStorage.setItem(HIDDEN_HINTS_KEY, JSON.stringify(hidden))
    }
}

/** Check if tour hint is hidden */
const isHintHidden = (tourId: string): boolean => {
    return getHiddenHints().includes(tourId)
}

interface TourHintProps {
    className?: string
}

/**
 * TourHint - Floating hint at bottom-right showing available tour
 * Clean, minimal design with smooth animations
 */
const TourHint = ({ className }: TourHintProps) => {
    const location = useLocation()
    const { startTour } = useTour()
    const { hasCustomHandler, executeHandler } = useTourHandler()
    const { hasOptions: hasQuickHelp } = useQuickHelpContext()
    const [availableTour, setAvailableTour] = useState<FeatureTour | null>(null)
    const [dismissedThisSession, setDismissedThisSession] = useState(false)
    const [isHovered, setIsHovered] = useState(false)

    useEffect(() => {
        setDismissedThisSession(false)

        const allTours = getAllTours()
        const tourForRoute = allTours.find(
            (tour) =>
                location.pathname.startsWith(tour.route) &&
                !isHintHidden(tour.id)
        )

        setAvailableTour(tourForRoute || null)
    }, [location.pathname])

    // Show hint if either: tour from release notes exists OR custom handler registered
    const shouldShowHint = availableTour || hasCustomHandler
    const tourName = availableTour?.name || 'Hướng dẫn sử dụng'
    const tourId = availableTour?.id || `custom-${location.pathname}`

    const handleStartTour = () => {
        // Use custom handler if page registered one (for multi-step forms)
        if (hasCustomHandler) {
            executeHandler()
            return
        }

        // Default: use simple tour steps
        if (availableTour) {
            const tourSteps = availableTour.steps.map((step) => ({
                selector: step.selector,
                content: step.content,
                title: step.title,
                position: step.position,
            }))
            startTour(tourSteps, () => {
                markTourCompleted(availableTour.id)
            })
        }
    }

    const handleDismissSession = (e: React.MouseEvent) => {
        e.stopPropagation()
        setDismissedThisSession(true)
    }

    const handleHidePermanently = (e: React.MouseEvent) => {
        e.stopPropagation()
        hideHintPermanently(tourId)
        setAvailableTour(null)
        setDismissedThisSession(true)
    }

    if (!shouldShowHint || dismissedThisSession || isHintHidden(tourId)) {
        return null
    }

    return (
        <div
            className={cn(
                'fixed bottom-4 z-50',
                hasQuickHelp ? 'right-[56px]' : 'right-2',
                'animate-in slide-in-from-right-4 fade-in duration-500',
                className
            )}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Compact card - shifts left when QuickHelp is visible */}
            <div
                onClick={handleStartTour}
                className={cn(
                    'relative cursor-pointer',
                    'bg-white rounded-lg shadow-md border border-gray-100',
                    'transition-all duration-300 ease-out',
                    isHovered ? 'shadow-lg scale-[1.02]' : 'shadow-md'
                )}
            >
                {/* Close button - only show on hover */}
                <button
                    onClick={handleDismissSession}
                    className={cn(
                        'absolute -top-1.5 -right-1.5 p-0.5',
                        'bg-white rounded-full shadow-sm border border-gray-100',
                        'text-gray-400 hover:text-gray-600 hover:bg-gray-50',
                        'transition-all duration-200',
                        isHovered
                            ? 'opacity-100 scale-100'
                            : 'opacity-0 scale-75'
                    )}
                >
                    <X className="h-3 w-3" />
                </button>

                <div className="flex items-center gap-2 px-3 py-2">
                    {/* Icon - smaller */}
                    <div className="flex-shrink-0">
                        <div className="w-7 h-7 rounded-full bg-amber-50 flex items-center justify-center">
                            <Lightbulb className="h-3.5 w-3.5 text-amber-500" />
                        </div>
                    </div>

                    {/* Content - compact */}
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-900 truncate">
                            {tourName}
                        </p>
                    </div>

                    {/* Arrow - smaller */}
                    <div
                        className={cn(
                            'flex-shrink-0 text-gray-400',
                            'transition-transform duration-200',
                            isHovered ? 'translate-x-0.5' : ''
                        )}
                    >
                        <ChevronRight className="h-4 w-4" />
                    </div>
                </div>

                {/* "Don't show again" link - only show on hover */}
                <div
                    className={cn(
                        'border-t border-gray-50 px-3 py-1.5',
                        'transition-all duration-200 overflow-hidden',
                        isHovered ? 'max-h-8 opacity-100' : 'max-h-0 opacity-0'
                    )}
                >
                    <button
                        onClick={handleHidePermanently}
                        className="text-[10px] text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        Không hiển thị nữa
                    </button>
                </div>
            </div>
        </div>
    )
}

export default TourHint
